
import { Button, Chip, CircularProgress, FormGroup, FormLabel, Tab, Tabs, TextField } from '@material-ui/core'
import Snackbar from '@material-ui/core/Snackbar'
import AddIcon from '@material-ui/icons/Add'
import ArrowForwardIcon from '@material-ui/icons/ArrowForward'
import { Alert, AlertTitle, Autocomplete } from '@material-ui/lab'
import { AxiosError, AxiosResponse } from 'axios'
import React, { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import 'src/assets/css/components/addDataForm.css'
import 'src/assets/css/components/pipelineType.css'
import useStyles from 'src/assets/css/components/FormStyles'
import chainIcon from 'src/assets/images/svgs/chain-icon.svg'
import dataIcon from 'src/assets/images/svgs/data.svg'
import CustomAccordion from 'src/components/CustomAccordion'
import EntityName from 'src/components/EntityName'
import DataPluginSettings from 'src/components/DataPluginSettings'
import DataScheduleComponent from 'src/components/DataScheduleComponent'
import DataTransform from 'src/components/DataTransform'
import EntitySelectDialog from 'src/components/EntitySelectDialog'
import ImageHolder from 'src/components/ImageHolder'
import JobLogButton from 'src/components/JobLogButton'
import SplitButton from 'src/components/SplitButton'
import { useAPIContext } from 'src/contexts/api'
import { useWindowContext } from 'src/contexts/window'
import { useWorkspaceContext } from 'src/contexts/workspace'
import { useDataPlugin, usePipeline } from 'src/hooks/useCRUDEntity'
import { IPipelineFormProps } from 'src/models/components/IPipelineForm'
import { TransformType } from 'src/models/components/IDataTransform'
import { ImageHolderSize } from 'src/models/components/IImageHolder'
import EntityRel from 'src/models/EntityRel'
import IDataComponent from 'src/models/IDataComponent'
import IDataPlugin, { SettingKind } from 'src/models/IDataPlugin'
import { INamedEntity } from 'src/models/IEntity'
import IError from 'src/models/IError'
import IPage from 'src/models/IPage'
import IPipeline, { PipelineStatus, PipelineType } from 'src/models/IPipeline'
import IJob, { JobStatus, JobType } from 'src/models/IJob'
import IngestionSplitDialog from 'src/components/IngestionSplitDialog'
import { logError } from 'src/utils/Error'
import ConfirmDialog from './ConfirmDialog'
import { PartialExcluding } from 'src/utils/types'
import IValidationError from 'src/models/IValidationError'
import StreamSelector from 'src/components/StreamSelector'
import { componentPluginType, INGESTION_PLUGIN_TYPES, isBackfill, loadersWithoutSource, ownExtractorName, pipelineType, pipelineTypeLabel, withComponent } from 'src/utils/pipelineType'

/**
 * Verify running on an ingestion's behalf, to test the connection and find the source's streams, or
 * Discover, which only finds them (MEL-702).
 */
enum Discovery {
    Running = 'running',
    Failed = 'failed',
    Lost = 'lost',
    // found the streams: said until the next save or discovery (MEL-753)
    Done = 'done',
}

type Component = PartialExcluding<IDataComponent, 'name'>

/** An ingestion's own extractor (`<source>--<ingestion>`) is shown as the source it inherits (MEL-702). */
const displayName = (dc: Component) => dc.inheritFrom || dc.name

const sourceOf = (components: Component[]) => components.find(dc => componentPluginType(dc) === 'EXTRACTOR')

/** The source's own settings, saved in the pipeline - not its stream selection - to tell when they change. */
const sourceSettings = (pipeline: Partial<IPipeline>) => {
    const source = sourceOf(pipeline._embedded?.dataComponents || [])
    return source && JSON.stringify(Object.entries(pipeline.properties || {})
        .filter(([k]) => k.startsWith(`${source.name}.`) && !k.match(/\._(select|metadata)$/))
        .sort())
}

const PipelineForm = (props: IPipelineFormProps): JSX.Element => {

    const classes = useStyles()

    const { catalogAPI } = useAPIContext()
    const { size } = useWindowContext()

    const workspace = props.workspace || useWorkspaceContext().workspace
    const { profile, pipelines, fetchPipelines, isLoadingPipelines } = useWorkspaceContext()

    const history = useHistory()

    // The API omits the commit link while the Review and commit feature is disabled.
    const reviewCommitEnabled = !!workspace?._links?.['commit']

    const pipeline = usePipeline(catalogAPI, props.pipeline, true)
    const dataPlugin = useDataPlugin(catalogAPI, props.dataPlugin)

    /*
     * MEL-702: the pipeline's type, set when it is created. A pipeline started from a plugin (the
     * "+ Pipeline" button, and onboarding) is an ingestion, whatever the API's new-pipeline template
     * says: that defaults to a workflow. Otherwise the type comes with the pipeline, from the API or
     * from the ?type= it was created with.
     */
    const [type] = useState(pipeline.canCreate && dataPlugin.canRead
        ? PipelineType.Ingestion
        : props.pipeline.type)
    const isIngestion = type === PipelineType.Ingestion

    /*
     * MEL-702 auto-discover: once an ingestion is published - which checks its source's required
     * settings, however the plugin makes them conditional - Verify runs by itself, to test the
     * connection and find the streams, if the source has none yet or its settings changed. The form
     * follows that Verify job itself, as a run started meanwhile becomes the pipeline's latest job.
     */
    const latestJob = props.pipeline._embedded?.['latest job']
    // a Discover is followed for any pipeline that has one (a workflow with a tap and a target too)
    const discoveryJobRef = useRef((isIngestion && latestJob?.type === JobType.PipelineVerify || latestJob?.type === JobType.PipelineDiscover)
        && [JobStatus.Queued, JobStatus.Running].includes(latestJob!.status) ? latestJob!._links?.self?.href : undefined)
    // only finding the streams, not testing the connection
    const isDiscoverOnlyRef = useRef(latestJob?.type === JobType.PipelineDiscover)
    const [discovery, setDiscovery] = useState(discoveryJobRef.current ? Discovery.Running : undefined)
    // how many streams the discovery found, and whether this save started one, to say so (MEL-753)
    const [foundStreams, setFoundStreams] = useState(0)
    const isVerifyStartedRef = useRef(false)
    const discoveredSettingsRef = useRef(sourceSettings(props.pipeline))
    // the check that failed, whose log says what went wrong: checks are left out of the pipeline's runs
    const [failedCheck, setFailedCheck] = useState<IJob>()

    // a draft ingestion holding dbt or another tool can move it to a workflow that runs after it
    const [isSplitOpen, setSplitOpen] = useState(false)
    const isSplitDoneRef = useRef(false)

    const [error, setError] = useState<{
        message: string | undefined,
        errors: IValidationError[],
    }>()
    const unsetError = () => setError(undefined)

    const [properties, setProperties] = useState(pipeline.properties)
    const [isSaved, setSaved] = useState(false)

    const [label, setLabel] = useState(pipeline.label || pipeline.name || dataPlugin.label || dataPlugin.name)
    const userModifiedLabelRef = useRef(!!label)

    const setLabelFromDataPlugin = (dataPlugin: IDataPlugin | undefined) => {
        if (userModifiedLabelRef.current)
            return

        setLabel(dataPlugin?.label || dataPlugin?.name || "")
    }

    const setInstanceLabel = (fieldName: string, value: string) => {
        setLabel(value)
        userModifiedLabelRef.current = true
    }

    const [actions, setActions] = useState(pipeline.actions)

    const [script, setScript] = useState(pipeline.script)
    const [transform, setTransform] = useState(script ? TransformType.Script : actions.length ? TransformType.Custom : TransformType.Default)

    // a new ingestion runs daily unless the user picks otherwise
    const [schedule, setSchedule] = useState(pipeline.schedule ?? (isIngestion && pipeline.canCreate ? '@daily' : undefined))
    const [scheduleType, setScheduleType] = useState<string>()

    const [timeout, setTimeout] = useState(pipeline.timeout)
    const [maxRetries, setMaxRetries] = useState(pipeline.maxRetries)
    const [dataComponents, setDataComponents] = useState<PartialExcluding<IDataComponent, 'name'>[]>(pipeline._embedded?.dataComponents || [])
    const [triggeredBy, setTriggeredBy] = useState(pipeline.triggeredBy)
    const [activeTab, setActiveTab] = useState(0)

    const draftRef = useRef(false)
    const dataComponentRef = useRef<string>()

    const [removeDialogOpen, setRemoveDialogOpen] = useState(false)
    const closeRemoveDialog = () => setRemoveDialogOpen(false)
    const openRemoveDialog = () => setRemoveDialogOpen(true)

    const [selectDialogOpen, setSelectDialogOpen] = useState(false)
    const closeSelectDialog = () => setSelectDialogOpen(false)
    const openSelectDialog = () => setSelectDialogOpen(true)

    const [dataComponentPage, setDataComponentPage] = useState<IPage<IDataComponent>>()
    const [dataPluginPage, setDataPluginPage] = useState<IPage<IDataPlugin>>()

    const handleAlertDismissed = () => setSaved(false)
    const handleTabChange = (_event: React.ChangeEvent<unknown>, tab: number) => setActiveTab(tab)

    const discoverStreams = async (saved: IPipeline) => {
        const source = sourceOf(saved._embedded?.dataComponents || [])
        const verifyLink = saved._links?.['verify pipeline']
        const settings = sourceSettings(saved)

        if (!isIngestion || !source || !verifyLink || (source.streams?.length && settings === discoveredSettingsRef.current))
            return

        discoveredSettingsRef.current = settings
        await catalogAPI.post(verifyLink.href)
            .then((r: AxiosResponse<IJob>) => {
                discoveryJobRef.current = r.data._links.self.href
                isDiscoverOnlyRef.current = false
                isVerifyStartedRef.current = true
                setDiscovery(Discovery.Running)
            })
            .catch(logError)
    }

    // the Streams tab's "Discover Streams": find them again without a connection test, e.g. once the source has
    // new tables. The API offers it to an ingestion, or a workflow with a tap and a target
    const discoverLink = pipeline._links?.['discover pipeline']
    const discoverAgain = async () => {
        if (!discoverLink)
            return

        await catalogAPI.post(discoverLink.href)
            .then((r: AxiosResponse<IJob>) => {
                discoveryJobRef.current = r.data._links.self.href
                isDiscoverOnlyRef.current = true
                setDiscovery(Discovery.Running)
            })
            .catch(logError)
    }

    const handleResponse = async (response: Promise<AxiosResponse<IPipeline>>) => {

        // started before the saved callback, which may open the saved pipeline afresh
        const handleSuccess = async (r: AxiosResponse<IPipeline>) => {
            if (!draftRef.current && r?.data)
                await discoverStreams(r.data)
            setSaved(true)
        }

        const handleError = (e: AxiosError<IError | IValidationError[]>) => {
            const data = e.response?.data

            if (!data)
                return

            const errors = Array.isArray(data) ? data : data.errors || []
            setError({
                message: Array.isArray(data) ? data[0].defaultMessage : data.message,
                errors: errors,
            })

            // settings to fix are shown on the Settings tab, whichever tab the save was made from
            if (errors.some(e => e.field?.startsWith('properties.')))
                setActiveTab(0)
        }

        await response.then(handleSuccess).catch(handleError)
    }

    const handleSave = async (options?: Partial<{ asDraft: boolean, run: boolean }>) => {
        const asDraft = options?.asDraft !== undefined ? options.asDraft : pipeline.status === PipelineStatus.Draft
        draftRef.current = asDraft
        isVerifyStartedRef.current = false
        setDiscovery(discovery => discovery === Discovery.Done ? undefined : discovery)

        // remove any properties for datacomponents that are no longer referenced by the pipeline
        const filteredProperties = properties && Object.entries(properties)
            .filter(([k]) => dataComponents.some(dc => k.startsWith(`${dc.name}.`)))
            .reduce((p, [k, v]) => ({ ...p, [k]: v }), {})

        setProperties(filteredProperties)

        const resolvedLabel = label ?? pipeline.label ?? pipeline.name ?? dataPlugin.label ?? dataPlugin.name ?? ''
        const effectiveLabel = resolvedLabel.trim()

        const data = {
            label: effectiveLabel,
            properties: filteredProperties,
            schedule: schedule,
            dataComponents: dataComponents.map(dc => {
                const dp = dc._embedded?.dataplugin

                // we know whether to handle a datacomponent or dataplugin based on the presence of an id or embedded dataplugin
                if (dc.id || !dp)
                    return dc.name

                return `${dp.pluginType}/${dp.name}${dp.variant ? `--${dp.variant}` : ""}`
            }),
            // an ingestion runs its source into its destination and nothing else
            actions: isIngestion || transform === TransformType.Default ? [] : actions,
            script: !isIngestion && transform !== TransformType.Default && script?.length ? script : undefined,
            timeout: timeout,
            maxRetries: maxRetries,
            triggeredBy: triggeredBy,
            // set on create; on update it is the stored type, which the API keeps (MEL-702)
            ...(type && { type }),
        }

        const { draft, create, update } = pipeline
        const save = asDraft ? draft : pipeline.canCreate ? create : update

        unsetError()
        await handleResponse(save(data).then(r => options?.run
            ? catalogAPI.post(r.data._links['create job'].href).then(() => r)
            : r))
    }

    const handleSubmit = () => handleSave()
    const handleSubmitAsDraft = () => handleSave({ asDraft: true })
    const handleSubmitAsReady = () => handleSave({ asDraft: false })
    const handleSubmitAndRun = () => handleSave({ asDraft: false, run: true })
    const isJobRunning = !!pipeline._links['withdraw job']

    const onChangeActions = (actions: string[]) => setActions(actions)

    const handleChangeTransform = (transform: TransformType) => {
        setTransform(transform)

        if (transform === TransformType.Default) setScript(undefined)
    }

    const handleChangeSchedule = (e: React.ChangeEvent<HTMLInputElement>) => {
        const scheduleType = e.target.value
        setScheduleType(scheduleType)

        if (scheduleType === 'manual-schedule') {
            setSchedule(undefined);
        } else if (scheduleType !== 'custom-schedule') {
            // set hourly or daily schedule
            setSchedule(scheduleType)
        }
    }

    /*
     * An ingestion's components always run source, then mappers, then destination - `meltano run`
     * takes them in this order - whichever order they were added in. A workflow keeps the order given.
     */
    const ordered = (components: PartialExcluding<IDataComponent, 'name'>[]) => !isIngestion
        ? components
        : components
            .map((dc, i) => ({ dc, i, rank: INGESTION_PLUGIN_TYPES.indexOf(componentPluginType(dc) || '') }))
            .sort((a, b) => a.rank - b.rank || a.i - b.i)
            .map(({ dc }) => dc)

    // a workflow takes a new source before the data store waiting for one (withComponent)
    const added = (component: PartialExcluding<IDataComponent, 'name'>) => isIngestion
        ? ordered([...dataComponents, component])
        : withComponent(dataComponents, component)

    // errors the form has no field to show beside, e.g. "An ingestion needs a data store" (MEL-702),
    // said in place of the count of them
    const bannerErrors = (error?.errors || []).filter(e => e.field !== 'label' && !e.field?.startsWith('properties.'))

    /*
     * The settings errors, each keyed to the component the form shows it on. The first save of a new
     * ingestion validates its source as the extractor of its own it becomes, `<source>--<ingestion>`,
     * which the form still shows as the source (MEL-753).
     */
    const source = isIngestion ? sourceOf(dataComponents) : undefined
    const settingErrors = (error?.errors || [])
        .filter(e => e.field?.startsWith('properties.'))
        .map(e => {
            const ownPrefix = source && `properties.${source.name}--`
            const setting = ownPrefix && e.field.startsWith(ownPrefix) && e.field.substring(ownPrefix.length).match(/^[^.]+(\..+)$/)?.[1]
            return setting ? { ...e, field: `properties.${source!.name}${setting}` } : e
        })
    const settingErrorsOf = (dc: Component) => settingErrors.filter(e => e.field.startsWith(`properties.${dc.name}.`))

    // a settings error with no field on the form, e.g. for a hidden setting, or not keyed to a component
    const unplacedSettingErrors = settingErrors.reduce<string[]>((messages, e) => {
        const dc = dataComponents.find(dc => e.field.startsWith(`properties.${dc.name}.`))
        const name = dc ? e.field.substring(`properties.${dc.name}.`.length) : e.field.substring('properties.'.length)
        const setting = dc?._embedded?.dataplugin?.settings?.find(s => s.name === name)
        const isShown = !!setting && !setting.hidden && setting.kind !== SettingKind.Hidden

        return isShown ? messages : [...messages, `${dc ? `${displayName(dc)} · ` : ''}${setting?.label || name}: ${e.defaultMessage}`]
    }, [])

    // a workflow that runs its components in order, whose data store comes before any source, fails
    const unsourcedStores = isIngestion || transform !== TransformType.Default ? []
        : loadersWithoutSource(dataComponents).map(i => displayName(dataComponents[i]))

    /*
     * An ingestion's canvas is three slots in run order - source, mappers, data store - each holding
     * its component or an add button of its own, so removing one leaves the others where they are.
     * The add button offers only that slot's plugin type. A workflow takes any plugin but files.
     */
    // an ingestion's sync state belongs to its data store, so once it has run the store stays
    const isDataStoreFixed = isIngestion && !!pipeline.dataStoreFixed

    /*
     * MEL-753: the data store's settings this form changes that move where an ingestion that has run
     * lands its streams - the API says which they are for each loader. Its sync state carries on, so
     * the new place misses the rows loaded before: said, not refused.
     */
    const landingChanges = (dc: Component) => !isDataStoreFixed || componentPluginType(dc) !== 'LOADER' ? []
        : (dc._embedded?.dataplugin?.settings || [])
            .filter(s => dc._embedded?.dataplugin?.landingSettings?.includes(s.name))
            .map(s => {
                const key = `${dc.name}.${s.name}`
                const stored = dc.properties?.[s.name] ?? s.value
                return { label: s.label || s.name, from: pipeline.properties?.[key] || stored, to: properties?.[key] || stored }
            })
            .filter(c => (c.from || '') !== (c.to || ''))

    const [slotTypes, setSlotTypes] = useState<string[]>([])
    const ofPluginType = (pluginType: string) => dataComponents.filter(dc => componentPluginType(dc) === pluginType)
    const needsSource = isIngestion && !ofPluginType('EXTRACTOR').length
    const isAddable = (pluginType: string | undefined) => !pluginType?.match(/^(FILE)$/)
        && (!isIngestion || slotTypes.includes(pluginType?.toUpperCase() || ''))
    // the mapper slot shows once the workspace has a mapper plugin to add, or the ingestion holds one
    const hasMapperSlot = isIngestion && (!!ofPluginType('MAPPER').length
        || !!dataPluginPage?._embedded?.[EntityRel.DataPlugin.collection]?.some(dp => dp.pluginType?.toUpperCase() === 'MAPPER'))
    const slotTitle = slotTypes.includes('EXTRACTOR') ? 'Add a source'
        : slotTypes.includes('MAPPER') ? 'Add a mapper'
            : slotTypes.includes('LOADER') ? 'Add a data store' : undefined

    // another ingestion's own extractor is shown as its source, so it would look like the source twice;
    // the source itself is offered, and the API gives this pipeline an extractor of its own for it
    const isAnotherIngestionsOwn = (dc: IDataComponent) => !!dc.inheritFrom
        && pipelines.some((p: IPipeline) => p.id !== pipeline.id && !!p.name && ownExtractorName(dc.inheritFrom!, p.name) === dc.name)

    const openSlotDialog = (pluginType: string) => {
        setSlotTypes([pluginType])
        openSelectDialog()
    }

    // an ingestion is named after its source, whatever was added first
    const labelsFrom = (component: PartialExcluding<IDataComponent, 'name'>) =>
        !isIngestion || componentPluginType(component) === 'EXTRACTOR'

    const onSelectEntity = (entity: INamedEntity, entityRel: EntityRel) => {
        if (entityRel == EntityRel.DataPlugin) {
            const plugin = entity as IDataPlugin
            const component = {
                //  do not map id so we know that we are processing a dataplugin
                name: plugin.name,
                commands: plugin.commands || {},
                _embedded: { dataplugin: plugin }
            }
            setDataComponents(added(component))
            labelsFrom(component) && setLabelFromDataPlugin(plugin)
        } else if (entityRel == EntityRel.DataComponent) {
            const component = entity as IDataComponent
            setDataComponents(added(component))
            labelsFrom(component) && setLabelFromDataPlugin(component._embedded.dataplugin)
        }
    }

    const canModifyComponents = !profile?.featuresDisabled?.includes("MODIFY_PIPELINE")

    const componentHolder = (dc: PartialExcluding<IDataComponent, 'name'>, i: number) => {
        const defaultIcon = componentPluginType(dc) === 'LOADER' ? dataIcon : chainIcon

        const onRemoveDataComponent = () => {
            dataComponentRef.current = dc.name
            openRemoveDialog()
        }

        const isRemovable = canModifyComponents && !(isDataStoreFixed && componentPluginType(dc) === 'LOADER')

        return <ImageHolder
            key={dc.id || `${dc.name}-${i}`}
            label={displayName(dc)}
            imageUrl={dc._embedded?.dataplugin.logoUrl || dc.dataPluginLogoUrl || defaultIcon}
            size={ImageHolderSize.Small}
            overlay={isRemovable && "Remove"}
            onClick={isRemovable ? onRemoveDataComponent : () => void 0} />
    }

    // an ingestion slot's own add button, the same for every slot
    const addButton = (pluginType: string, label: string) => canModifyComponents &&
        <Button
            className='image-holder-small'
            aria-label={`add-pipeline-${label.toLowerCase().replace(' ', '-')}`}
            title={`Add a ${label.toLowerCase()}`}
            color='primary'
            variant='outlined'
            disabled={pipeline.isProcessing}
            onClick={() => openSlotDialog(pluginType)}>
            <div className='pipeline-add-component'>
                <AddIcon />
                <span>{label}</span>
            </div>
        </Button>

    const onConfirmRemoveComponent = async () => {
        setDataComponents(dataComponents.filter((_dc, i) => i !== dataComponents.map(dc => dc.name).lastIndexOf(dataComponentRef.current!)))
    }

    useEffect(() => {
        if (props.autoValidate)
            handleSave({ asDraft: false })

        // ?disable=true — stop the pipeline running without making the user find "Save as draft"
        // in the save dropdown. Mutually exclusive with autoValidate in practice; if both are
        // somehow set, validating wins because it is the more deliberate action.
        //
        // Guarded on canDraft, not on `pipeline.draft`: the latter is always a function that throws
        // internally. This fires with no click and the link arrives by email, so a recipient who does
        // not administer the workspace can land here — the API omits the draft link for them, and
        // attempting it would throw rather than simply leaving the pipeline alone.
        else if (props.autoDisable && pipeline.canDraft)
            handleSave({ asDraft: true })

        if (!pipelines.length && !isLoadingPipelines)
            fetchPipelines(workspace)

        // a new ingestion loads into the workspace's default data store until the user picks another;
        // one started from a plugin has its source already
        const defaultDataStore = (props.defaultDataStore || workspace._embedded?.['default datastore']) as IDataComponent | undefined
        const defaultStore = isIngestion && pipeline.canCreate && defaultDataStore && !dataComponents.length
            // a data store is a loader, whether or not its plugin came with it
            ? [componentPluginType(defaultDataStore) ? defaultDataStore : { ...defaultDataStore, dataPluginType: 'LOADER' }]
            : []

        if (pipeline.canCreate && !dataPlugin.canRead && defaultStore.length)
            setDataComponents(defaultStore)

        if (!pipeline.canCreate || !dataPlugin.canRead) return

        setDataComponents(ordered([
            ...dataComponents,
            { name: `extractors/${dataPlugin.name}--${dataPlugin.variant}` },
            // dbt never runs in an ingestion: it belongs in a workflow that runs after it
            ...(isIngestion ? defaultStore : [{ name: 'transformers/dbt' }])
        ]))
    }, [])

    useEffect(() => {
        props.onDataComponentsChange?.(dataComponents)
        setLabelFromDataPlugin((isIngestion ? sourceOf(dataComponents) : dataComponents[0])?._embedded?.dataplugin)

        if (!pipeline.canCreate || !dataPlugin.canRead || !dataComponents.length) return

        handleSave({ asDraft: true })
    }, [dataComponents])

    useEffect(() => {
        isSaved && props.onSaved(pipeline, draftRef.current)
    }, [isSaved])

    useEffect(() => {
        // an ingestion needs the plugins up front, to tell whether there is a mapper to offer
        if (!selectDialogOpen && !isIngestion) return

        // one page of everything: the dialog filters what it's given, and a next page from the API
        // would come back unfiltered
        const dataPluginsLink = workspace?._links?.dataplugins
        dataPluginPage || !dataPluginsLink || catalogAPI
            .get(dataPluginsLink.href, { params: { size: 999 } })
            .then((r: AxiosResponse<IPage<IDataPlugin>>) => setDataPluginPage(r.data))
            .catch(logError)

        if (!selectDialogOpen) return

        dataComponentPage || catalogAPI
            .get(workspace._links.datacomponents.href, { params: { size: 999 } })
            .then((r: AxiosResponse<IPage<IDataComponent>>) => setDataComponentPage(r.data))
    }, [selectDialogOpen])

    useEffect(() => {
        if (!pipeline.dataComponents.length || error || !isSaved)
            return

        const saved = pipeline._embedded!.dataComponents
        // a new ingestion's source becomes an extractor of its own on save, its settings with it (MEL-702)
        if (saved.some(dc => !dataComponents.some(c => c.name === dc.name)))
            setProperties(pipeline.properties)
        setDataComponents([...saved])
    }, [pipeline.dataComponents, error, isSaved])

    // follow the Verify run, then show the streams it found
    useEffect(() => {
        if (discovery !== Discovery.Running || !pipeline._links.self)
            return

        // a run the form can't see is given up on, rather than followed forever
        let errors = 0
        const interval = setInterval(() => catalogAPI.get(discoveryJobRef.current!)
            .then(async (r: AxiosResponse<IJob>) => {
                const { status } = r.data
                if (status === JobStatus.Complete) {
                    const found: IDataComponent[] = await catalogAPI.get(pipeline._links.self.href)
                        .then((p: AxiosResponse<IPipeline>) => p.data._embedded?.dataComponents || [])
                    setDataComponents(components => components.map(dc =>
                        ({ ...dc, streams: found.find(f => f.name === dc.name)?.streams ?? dc.streams })))
                    setFoundStreams(sourceOf(found)?.streams?.length || 0)
                }
                if (![JobStatus.Queued, JobStatus.Running].includes(status)) {
                    setFailedCheck(status === JobStatus.Complete ? undefined : r.data)
                    setDiscovery(status === JobStatus.Complete ? Discovery.Done : Discovery.Failed)
                }
                errors = 0
            })
            .catch((e: AxiosError) => {
                logError(e)
                if (++errors >= 3)
                    setDiscovery(Discovery.Lost)
            }), 5000)

        return () => clearInterval(interval)
    }, [discovery])

    useEffect(() => {
        if (label)
            return

        userModifiedLabelRef.current = false
    }, [label])

    const tasks = ["Deploy"]

    return <>
        <ConfirmDialog
            isOpen={removeDialogOpen}
            title={`Remove '${dataComponentRef.current}'?`}
            message={`Remove this component from the pipeline '${label}'.`}
            confirmLabel="Remove"
            handleConfirm={onConfirmRemoveComponent}
            handleClose={closeRemoveDialog}
        />
        {dataComponentPage && dataPluginPage &&
            <EntitySelectDialog
                // the dialog keeps the page it opened with, so start a new one when what's offered changes
                key={isIngestion ? slotTypes.join() : 'any'}
                open={selectDialogOpen}
                title={isIngestion ? slotTitle : undefined}
                tabs={{
                    "Existing": {
                        description: "Add an existing project component",
                        entityRel: EntityRel.DataComponent,
                        page: {
                            ...dataComponentPage,
                            _embedded: dataComponentPage._embedded && { [EntityRel.DataComponent.collection]: dataComponentPage._embedded[EntityRel.DataComponent.collection].filter(dc => isAddable(dc._embedded.dataplugin.pluginType) && !isAnotherIngestionsOwn(dc)) },
                        },
                        getEntityLogoUrl: (e: IDataComponent) => e._embedded.dataplugin.logoUrl,
                        getEntityDescription: (e: IDataComponent) => e._embedded.dataplugin.description
                    },
                    "New": {
                        description: "Add a new project component from an installed plugin",
                        entityRel: EntityRel.DataPlugin,
                        page: {
                            ...dataPluginPage,
                            _embedded: dataPluginPage._embedded && { [EntityRel.DataPlugin.collection]: dataPluginPage._embedded[EntityRel.DataPlugin.collection].filter(dp => isAddable(dp.pluginType)) },
                        },
                        getEntityLogoUrl: (e: IDataPlugin) => e.logoUrl,
                        getEntityDescription: (e: IDataPlugin) => e.description
                    }
                }}
                onClose={closeSelectDialog}
                onSelect={onSelectEntity} />}
        <div className={!size.smallScreen ? classes.root_medium : classes.root}>
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={isSaved}
                autoHideDuration={reviewCommitEnabled && !draftRef.current ? 10000 : 6000}
                onClose={handleAlertDismissed}>
                <Alert
                    data-testid='saved-datastore'
                    variant='filled'
                    severity='success'
                    action={(reviewCommitEnabled && !draftRef.current && workspace?.id &&
                        <Button
                            data-testid='saved-review-commit'
                            color='inherit'
                            size='small'
                            onClick={() => history.push(`/lab/${workspace.id}/settings?review=1`)}>
                            Review &amp; commit
                        </Button>) || undefined}>
                    {`'${pipeline.label || pipeline.name}' saved${draftRef.current
                        ? ' as draft'
                        : `${isVerifyStartedRef.current ? `. Testing the connection and finding its streams: you can leave this page` : ''}`
                            + (reviewCommitEnabled ? '. Review & commit to publish it to your repository.' : '')}`}
                </Alert>
            </Snackbar>
            {error &&
                <Alert
                    severity='error'
                    data-testid='pipeline-save-error'>
                    {/* settings to fix are counted, not the backend's "N validation errors from 'resource'" */}
                    {settingErrors.length > 0 &&
                        <AlertTitle>
                            {`${settingErrors.length} ${settingErrors.length > 1 ? 'settings need' : 'setting needs'} fixing before this pipeline can be saved.`}
                            {pipeline.canDraft && ' To keep your changes meanwhile, use Save as draft.'}
                        </AlertTitle>}
                    {bannerErrors.length || settingErrors.length
                        ? [...bannerErrors.map(e => e.defaultMessage), ...unplacedSettingErrors].map((m, i) => <div key={i}>{m}</div>)
                        : error.message}
                </Alert>}
            {!!pipeline.problems?.length &&
                <Alert
                    severity='error'
                    data-testid='pipeline-problems'
                    action={(isIngestion && pipeline.canUpdate && workspace.managed !== false
                        && (pipeline._embedded?.dataComponents || []).some(dc => !INGESTION_PLUGIN_TYPES.includes(componentPluginType(dc) || ''))
                        && <Button color='inherit' size='small' onClick={() => setSplitOpen(true)}>
                            Move to a workflow
                        </Button>) || undefined}>
                    <AlertTitle>{`This draft won't run until it's fixed`}</AlertTitle>
                    <ul className='pipeline-problems'>
                        {pipeline.problems.map(p => <li key={p}>{p}</li>)}
                    </ul>
                    {pipeline.repositoryPath && (workspace.managed === false
                        ? `It was deployed from ${pipeline.repositoryPath}: fix that file in your repository.`
                        : `It was deployed from ${pipeline.repositoryPath}: fix it here and save, or fix that file.`)}
                </Alert>}
            {isSplitOpen &&
                <IngestionSplitDialog
                    open={isSplitOpen}
                    pipeline={pipeline as IPipeline}
                    onDone={() => { isSplitDoneRef.current = true }}
                    onClose={() => {
                        setSplitOpen(false)
                        isSplitDoneRef.current && props.onClose()
                    }} />}
            {type &&
                <div className='pipeline-form-type'>
                    <span className={`pipeline-type-chip ${type.toLowerCase()}`}>
                        {pipelineTypeLabel(type)}
                    </span>
                    <span className='pipeline-form-type-note'>
                        {isIngestion
                            ? 'Copies one source into one data store.'
                            : 'Runs dbt or any other tools, on a schedule or after other pipelines.'}
                        {' To do something different, create another pipeline.'}
                    </span>
                </div>}
            <EntityName
                instanceName={label}
                handleNameChange={setInstanceLabel}
                error={error?.errors.find(e => e.field === 'label' )}
                fieldName='label'
                fieldLabel='Label' />
            <div className='inline-vertical-center pipeline-row-data image-holders'>
                {isIngestion
                    ? <>
                        <div className='pipeline-slot' data-testid='pipeline-slot-source'>
                            {ofPluginType('EXTRACTOR').map(componentHolder)}
                            {needsSource && addButton('EXTRACTOR', 'Source')}
                        </div>
                        {hasMapperSlot && <>
                            <ArrowForwardIcon className='pipeline-slot-arrow' aria-hidden />
                            <div className='pipeline-slot' data-testid='pipeline-slot-mapper'>
                                {ofPluginType('MAPPER').map(componentHolder)}
                                {addButton('MAPPER', 'Mapper')}
                            </div>
                        </>}
                        <ArrowForwardIcon className='pipeline-slot-arrow' aria-hidden />
                        <div className='pipeline-slot' data-testid='pipeline-slot-datastore'>
                            {ofPluginType('LOADER').map(componentHolder)}
                            {!ofPluginType('LOADER').length && addButton('LOADER', 'Data store')}
                        </div>
                        {/* anything else, e.g. dbt a pipeline file added: shown so it can be removed */}
                        {dataComponents.filter(dc => !INGESTION_PLUGIN_TYPES.includes(componentPluginType(dc) || '')).map(componentHolder)}
                    </>
                    : <>
                        {dataComponents.map(componentHolder)}
                        {canModifyComponents &&
                        <Button
                            className='image-holder-small'
                            aria-label='add-pipeline-component'
                            color='primary'
                            variant='outlined'
                            disabled={pipeline.isProcessing}
                            onClick={openSelectDialog}>
                            <div className='pipeline-add-component'>
                                <AddIcon />
                            </div>
                        </Button>}
                    </>}
            </div>
            {unsourcedStores.length > 0 &&
                <Alert severity='warning' data-testid='pipeline-order-warning'>
                    {`${unsourcedStores.map(name => `'${name}'`).join(', ')} ${unsourcedStores.length > 1 ? 'run' : 'runs'} before any source, so this pipeline will fail. `
                        + `Remove ${unsourcedStores.length > 1 ? 'them' : 'it'} and add ${unsourcedStores.length > 1 ? 'them' : 'it'} again after ${unsourcedStores.length > 1 ? 'their sources' : 'its source'}.`}
                </Alert>}
            {isDataStoreFixed &&
                <div className='pipeline-form-type-note' data-testid='pipeline-datastore-fixed'>
                    {`This ingestion has run, so its data store can't change. To load into another one, create a new ingestion.`}
                </div>}
            {discovery === Discovery.Running &&
                <div className='pipeline-discovery' data-testid='pipeline-discovery'>
                    <CircularProgress size='1em' />
                    <span>{isDiscoverOnlyRef.current ? "Discovering the source's streams…" : "Testing the connection and finding the source's streams…"}</span>
                    <span>This can take a few minutes. You can leave this page: the pipelines list shows how it went.</span>
                </div>}
            {discovery === Discovery.Done &&
                <Alert
                    severity='success'
                    data-testid='pipeline-discovery-done'
                    onClose={() => setDiscovery(undefined)}
                    action={foundStreams > 0 &&
                        <Button color='inherit' size='small' data-testid='pipeline-choose-streams' onClick={() => setActiveTab(1)}>
                            Choose streams
                        </Button>}>
                    {`${isDiscoverOnlyRef.current ? '' : 'Connection OK. '}`
                        + (foundStreams > 0
                            ? `Found ${foundStreams} ${foundStreams > 1 ? 'streams' : 'stream'}: choose which to sync.`
                            : `The source has no streams to sync.`)}
                </Alert>}
            {(discovery === Discovery.Failed || discovery === Discovery.Lost) &&
                <Alert
                    severity='warning'
                    data-testid='pipeline-discovery-failed'
                    action={discovery === Discovery.Failed && failedCheck &&
                        <JobLogButton job={failedCheck} data-testid='pipeline-discovery-log' />}>
                    {isDiscoverOnlyRef.current
                        ? discovery === Discovery.Failed
                            ? `Couldn't find the source's streams, so it keeps the ones found before. Check its settings: the discovery's log says what went wrong.`
                            : `Couldn't follow the discovery. The pipelines list says how it went.`
                        : discovery === Discovery.Failed
                            ? `Couldn't connect to the source or find its streams. Check its settings and save again: the connection test's log says what went wrong.`
                            : `Couldn't follow the connection test. The pipelines list says how it went.`}
                </Alert>}
            <div>
                <Tabs
                    className='pipeline-form-tabs'
                    indicatorColor='primary'
                    value={activeTab}
                    onChange={handleTabChange}>
                    <Tab id='settings' label="Settings" />
                    <Tab
                        id='streams'
                        label="Streams"
                        disabled={!dataComponents.some(dc => dc.dataPluginType === 'extractor')}
                    />
                    {/* an ingestion runs no commands, so this tab holds only its timeout and retries */}
                    <Tab id='actions' label={isIngestion ? "Advanced" : "Actions"} />
                    <Tab id='triggers' label="Triggers" />
                </Tabs>
                {activeTab === 0 &&
                    <div className='datastore-properties'>
                        <div aria-labelledby='settings'>
                            {dataComponents.filter(dc => !!dc._embedded?.dataplugin).map((dc, i, filteredComponents) => {
                                const dataplugin = dc._embedded?.dataplugin

                                if (!dataplugin)
                                    return

                                const onToggle = (isExpanded: boolean) => props.onSettingsToggle?.(isExpanded && dataplugin.description ? i : undefined)
                                const errors = settingErrorsOf(dc)
                                const changes = landingChanges(dc)
                                const shown = (value?: string) => value ? `'${value}'` : 'its default'

                                return (
                                    <CustomAccordion
                                        key={dc.id || i}
                                        title={displayName(dc)}
                                        isLast={i === filteredComponents.length - 1}
                                        onToggle={onToggle}
                                        expanded={!!errors?.length || filteredComponents.length === 1}>
                                        {changes.length > 0 &&
                                            <Alert severity='warning' data-testid='pipeline-landing-changed'>
                                                {`This ingestion has already loaded data. Changing ${changes.map(c => `${c.label} from ${shown(c.from)} to ${shown(c.to)}`).join(' and ')} `
                                                    + `changes where it lands, and incremental streams only load what changed since the last run, so the new place won't have the earlier rows. `
                                                    + `To load everything there, create a new ingestion.`}
                                            </Alert>}
                                        <DataPluginSettings
                                            isEditEnabled
                                            selectedDataplugin={dataplugin}
                                            prefix={dc.name}
                                            properties={properties}
                                            readOnlyProperties={dc.properties}
                                            handleSettingsChange={(name: string, value: any) => setProperties({ ...properties, ...{ [name]: value || undefined } })}
                                            handleSettingsUpdate={setProperties}
                                            errors={errors} />
                                    </CustomAccordion>)})}
                        </div>
                    </div>}
                {activeTab === 1 &&
                    <div className='datastore-properties'>
                        <div aria-labelledby='streams'>
                            {dataComponents
                                .filter(dc => !!dc.streams)
                                .map((dc, i, filteredComponents) => {
                                    const selectPropertyKey = `${dc.name}._select`

                                    return (
                                        <CustomAccordion
                                            key={dc.id || i}
                                            title={displayName(dc)}
                                            isLast={i === filteredComponents.length - 1}
                                            expanded={filteredComponents.length === 1}>
                                            <StreamSelector
                                                streams={dc.streams || []}
                                                rules={properties[selectPropertyKey] && JSON.parse(properties[selectPropertyKey])}
                                                onRulesChange={(rules) => setProperties((properties) => {
                                                    delete properties[selectPropertyKey]

                                                    if (rules.length)
                                                        properties[selectPropertyKey] = JSON.stringify(rules)

                                                    return properties
                                                })} />
                                        </CustomAccordion>
                                    )})}
                            {!dataComponents.some(dc => !!dc.streams) &&
                                <FormLabel>{discovery === Discovery.Running ? "Finding the source's streams…" : "No streams available."}</FormLabel>}
                            {discoverLink &&
                                <div className='pipeline-discover-streams'>
                                    <Button
                                        variant='outlined'
                                        size='small'
                                        data-testid='pipeline-discover-streams'
                                        disabled={discovery === Discovery.Running}
                                        title="Ask the source for its streams again, e.g. once it has new tables. Nothing is moved, and the connection isn't tested."
                                        onClick={discoverAgain}>
                                        Discover Streams
                                    </Button>
                                </div>}
                        </div>
                    </div>}
                {activeTab === 2 &&
                    <div className='datastore-properties' aria-labelledby='actions'>
                        {isIngestion
                            ? <FormLabel>An ingestion runs its source into its data store and nothing else. To run dbt or other tools after it, create a workflow that runs after this ingestion.</FormLabel>
                            : <DataTransform
                            dataComponents={dataComponents}
                            actions={actions}
                            script={script}
                            transformType={transform}
                            handleCustomScriptChange={setScript}
                            onChangeActions={onChangeActions}
                            onChangeTransform={handleChangeTransform} />}
                        <TextField
                            label="Timeout (seconds)"
                            id={`${pipeline.id}--timeout`}
                            onChange={e => setTimeout(Math.max(0, Number(e.target.value)))}
                            error={timeout < 1}
                            helperText={timeout < 1 ? 'Enter a timeout of at least 1 second' : undefined}
                            type='number'
                            value={timeout || ""}
                            variant='outlined'
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                                // endAdornment: <InputAdornment position="end">{`second${timeout !== 1 ? "s" : ""}`}</InputAdornment>,
                                inputProps: { min: 1 }
                            }}/>
                        <TextField
                            label="Max retries"
                            id={`${pipeline.id}--max-retries`}
                            onChange={e => setMaxRetries(Number(e.target.value))}
                            type='number'
                            value={maxRetries}
                            variant='outlined'
                            InputLabelProps={{ shrink: true }}
                            InputProps={{ inputProps: { min: 0 } }}/>
                    </div>}
                {activeTab === 3 &&
                    <div className='datastore-properties' aria-labelledby='triggers'>
                        <FormGroup>
                            <FormLabel>Schedule</FormLabel>
                            <DataScheduleComponent
                                schedule={schedule}
                                selectedSchedule={scheduleType}
                                handleScheduleChange={setSchedule}
                                onChangeSchedule={handleChangeSchedule} />
                        </FormGroup>
                        <FormGroup>
                            <FormLabel>Pipelines and Tasks</FormLabel>
                            <Autocomplete
                                multiple
                                disableCloseOnSelect
                                fullWidth
                                autoHighlight
                                options={[
                                    ...pipelines.length
                                        // ingestions first, so each group is one run of options (MEL-702)
                                        ? [PipelineType.Ingestion, PipelineType.Workflow].reduce<string[]>((names, t) =>
                                            names.concat(pipelines.filter((p: IPipeline) => pipelineType(p) === t && !isBackfill(p)).map((p: IPipeline) => p.name)), [])
                                        : [''],  // dummy option to display loading spinner
                                    ...tasks,
                                ]}
                                getOptionDisabled={o => !o || o === pipeline.name}
                                getOptionSelected={(o, v) => isLoadingPipelines && !tasks.includes(o) || o === v}
                                groupBy={o => tasks.includes(o)
                                    ? "Tasks"
                                    : pipelineType(pipelines.find((p: IPipeline) => p.name === o) || {}) === PipelineType.Ingestion
                                        ? "Ingestions"
                                        : "Workflows"}
                                value={triggeredBy}
                                onChange={(e, triggeredBy) => setTriggeredBy(triggeredBy)}
                                renderOption={o => o || <CircularProgress size='1em' />}
                                renderInput={(params) =>
                                    <TextField
                                        {...params}
                                        variant='outlined'
                                    />}
                                renderTags={(options, getTagProps) =>
                                    options.map((o, i) =>
                                        <Chip
                                            {...getTagProps({ index: i })}
                                            key={o}
                                            label={o}
                                            disabled={isLoadingPipelines && !tasks.includes(o)}
                                        />
                                    )}
                            />
                        </FormGroup>
                    </div>}
            </div>
            <form
                onSubmit={handleSubmit}
                id='create-pipeline'>
                <div className='form-buttons'>
                    <SplitButton
                        disabled={!label || timeout < 1 || pipeline.isProcessing}
                        options={[{
                            label: "Save",
                            onClick: handleSubmitAsReady
                        }, {
                            label: "Save as draft",
                            onClick: handleSubmitAsDraft
                        }, {
                            label: "Save and run",
                            onClick: handleSubmitAndRun,
                            disabled: isJobRunning,
                            title: isJobRunning ? "A job is already running" : undefined
                        }]}
                        SpinnerProps={{ active: pipeline.isProcessing }} />
                    <Button onClick={props.onClose}>
                        Close
                    </Button>
                </div>
            </form>
        </div >
        {!size.smallScreen &&
            <div className='vl-help'></div>}
    </>

}

export default PipelineForm
